import { apiClient } from '@/core/api/client';
import {
    descarcaExercitiiPdfColab,
    descarcaRaspunsuriPdfColab,
    getCollabActivity,
    getCollabInfo,
    submitCollabActivity,
} from '@/api/generated/sdk.gen';
import type { CollabActivitySubmissionDto, CollabInfoDto, LessonDto } from '@/api/generated/types.gen';
import { ApiValidationError, getAxiosErrorMessage, getErrorStatus, getFieldErrors, handleResponse } from '@/core/api/response-handler';

type Pdf = { blob: Blob; dispozitie: string | null };

function caPdf(raspuns: unknown, blob: unknown): Pdf {
    const antete = (raspuns as { headers?: Record<string, unknown> }).headers;
    const dispozitie = antete?.['content-disposition'];
    return { blob: blob as Blob, dispozitie: typeof dispozitie === 'string' ? dispozitie : null };
}

/**
 * Rutele publice ale linkului de colaborator. Fara JWT: apiClient adauga Authorization doar daca
 * exista token in localStorage, iar backendul il ignora pe /api/collab/** (permitAll).
 *
 * 🔴 Pagina colaboratorului cheama DOAR ce e aici. O ruta autentificata (`/api/lessons/**`,
 * `/api/files/upload`) ar raspunde 401, iar interceptorul clientului ar incerca un refresh si ar
 * deconecta - pe o pagina care n-are sesiune.
 */
class CollabAPI {
    async getInfo(token: string): Promise<CollabInfoDto> {
        const response = await getCollabInfo({ path: { token } });
        return handleResponse(response);
    }

    /** Detaliile unei activitati din lista linkului: acelasi LessonDto ca la profesor. */
    async getActivity(token: string, lessonId: string): Promise<LessonDto> {
        const response = await getCollabActivity({ path: { token, lessonId } });
        return handleResponse(response);
    }

    /** Fisa de exercitii, ca Blob. Arunca la orice eroare (SDK-ul generat nu arunca singur). */
    async exercitiiPdf(token: string, lessonId: string): Promise<Pdf> {
        const response = await descarcaExercitiiPdfColab({ path: { token, lessonId } });
        return caPdf(response, handleResponse(response));
    }

    async raspunsuriPdf(token: string, lessonId: string): Promise<Pdf> {
        const response = await descarcaRaspunsuriPdfColab({ path: { token, lessonId } });
        return caPdf(response, handleResponse(response));
    }

    async submit(token: string, body: CollabActivitySubmissionDto): Promise<string> {
        const response = await submitCollabActivity({ path: { token }, body });
        // Raspunsul e Map<String, UUID> pe backend, deci tipul generat e un index signature.
        return handleResponse(response).lessonAnswerId as string;
    }

    /** Multipart direct prin axios: SDK-ul generat nu expune onUploadProgress. Poza sau PDF. */
    async uploadPhoto(token: string, file: File, onProgress: (pct: number) => void): Promise<{ fileId: string }> {
        const formData = new FormData();
        formData.append('file', file);
        try {
            const response = await apiClient.post<{ url: string }>(`/api/collab/${encodeURIComponent(token)}/files`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
                onUploadProgress: (e) => {
                    // e.total lipseste cand browserul nu poate calcula lungimea corpului (ex.
                    // stream fara Content-Length cunoscut) - un 0 acolo ar trimite bara de progres
                    // inapoi la zero in loc sa ramana pe ultima valoare cunoscuta.
                    if (e.total) onProgress(Math.round((e.loaded * 100) / e.total));
                },
            });
            return { fileId: response.data.url };
        } catch (error) {
            // Calea asta nu trece prin handleResponse (SDK-ul generat nu are onUploadProgress,
            // deci apelul e axios brut) - normalizam manual la ApiValidationError, ca getInfo/submit
            // si uploadPhoto sa arunce aceeasi forma de eroare pentru Task 14.
            // getAxiosErrorMessage (nu getErrorMessage): pe o eroare axios, `.message` e mesajul
            // TEHNIC al lui axios ("Network Error", "Request failed with status code 400"), nu
            // mesajul backendului (H2, fix round 1) - citim direct corpul raspunsului.
            throw new ApiValidationError(
                getAxiosErrorMessage(error, 'Încărcarea fișierului a eșuat.'),
                getFieldErrors(error),
                getErrorStatus(error),
            );
        }
    }
}

export const collabApi = new CollabAPI();
